import { API_URL } from "../../utils/constants";

/**
 * Click/interaction beacon.
 *
 * The API only ever recorded what each page *returned*, never what a visitor
 * opened - documents are served as direct S3/CloudFront links that never reach
 * the backend. This reports the interaction so the admin app can show real
 * per-document / per-plan / per-FAQ counts.
 *
 * Uses fetch + keepalive rather than navigator.sendBeacon: sendBeacon cannot set
 * the custom `token` header the client authorizer requires
 * (identitySource: method.request.header.token), so it would always 401.
 * keepalive lets the request survive the tab/navigation that a document click
 * usually triggers.
 *
 * Fire-and-forget by design - tracking must never block or break a download.
 */

const VALID_EVENT_TYPES = ["click", "view", "download", "expand"];
const VALID_ITEM_TYPES = [
  "document",
  "plan",
  "faq",
  "website",
  "vendor",
  "resource",
  "spd",
];

export const trackEvent = (payload = {}) => {
  try {
    const { event_type: eventType, item_type: itemType } = payload;

    // Mirror the server-side whitelist so a bad call fails loudly in dev
    // rather than silently generating 400s in production.
    if (!VALID_EVENT_TYPES.includes(eventType)) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`trackEvent: invalid event_type "${eventType}"`);
      }
      return;
    }
    if (!VALID_ITEM_TYPES.includes(itemType)) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`trackEvent: invalid item_type "${itemType}"`);
      }
      return;
    }

    fetch(`${API_URL}/v1/client/track`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        token: process.env.REACT_APP_DOCLIB_API_TOKEN,
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch (e) {
    // Never surface a tracking failure to the user.
  }
};

/**
 * Bind the employer/website/page context once so call sites only pass the item.
 * Returns a no-op-safe function - missing context is sent as "unknown" rather
 * than dropping the event, because `parentWebsite` arrives asynchronously via
 * postMessage and is null on first render.
 */
export const makeTracker = ({ employerIdForURL, employer_name, website, page }) => (
  item
) =>
  trackEvent({
    employerIdForURL: employerIdForURL || "unknown",
    employer_name: employer_name || "unknown",
    website: website || "unknown",
    page: page || "unknown",
    ...item,
  });

const trackSvc = { trackEvent, makeTracker };

export default trackSvc;
